/* Partner Management → Unmatched names: Hub product names not found in Product Alias. Pick the right product once —
   every open order row with that name gets the second name and the name is saved as an alias for next time. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

const h = () => ({ headers: getAuthHeader() });

const MapCell = ({ x, onDone }) => {
  const [v, setV] = useState("");
  const [sug, setSug] = useState([]);
  const [busy, setBusy] = useState(false);
  const t = useRef(null);
  const type = (s) => {
    setV(s); clearTimeout(t.current);
    if (s.trim().length < 2) { setSug([]); return; }
    t.current = setTimeout(() => axios.get(`${API}/product-aliases/suggest?q=${encodeURIComponent(s.trim())}&limit=10`, h())
      .then((r) => setSug(r.data.rows || [])).catch(() => setSug([])), 250);
  };
  const save = async (name) => {
    setBusy(true); setSug([]);
    try {
      const r = await axios.post(`${API}/portal-admin/unmatched-names/map`, { key: x.key, second_name: name || v }, h());
      toast.success(`${r.data.rows} row(s) → ${r.data.second_name}${r.data.aliases_added.length ? " · alias saved" : ""}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not map"); } finally { setBusy(false); }
  };
  return (
    <div className="relative flex items-center gap-1.5">
      <input value={v} onChange={(e) => type(e.target.value)} placeholder="Type 2+ letters…" className="h-8 w-60 rounded border px-2 text-xs uppercase" data-testid={`um-input-${x.key}`} />
      <Button size="sm" className="h-8 text-xs" disabled={busy || !v.trim()} onClick={() => save()} data-testid={`um-map-${x.key}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Map"}</Button>
      {sug.length > 0 && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-56 w-72 overflow-auto rounded border bg-white shadow-lg" data-testid={`um-sug-${x.key}`}>
          {sug.map((s) => <button key={s} type="button" onMouseDown={(e) => { e.preventDefault(); setV(s); save(s); }}
            className="block w-full border-b px-2 py-1.5 text-left text-[11px] font-semibold hover:bg-indigo-50" data-testid={`um-sug-item-${s}`}>{s}</button>)}
        </div>)}
    </div>
  );
};

export const UnmatchedNamesTab = () => {
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/portal-admin/unmatched-names`, h()).then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load"));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" data-testid="um-tab">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-xs text-slate-500">Hub order names not found in Product Alias — <b data-testid="um-count">{d.items.length} name(s) · {d.total_rows} row(s)</b>. Map once: all those rows get the second name and the name is saved as an alias, so future uploads match by themselves.</p>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={load} data-testid="um-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
      </div>
      {!d.items.length ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="um-empty">All Hub product names are matched.</p> : (
        <div className="rounded-lg border">
          <table className="w-full text-xs"><thead className="bg-slate-50 text-left text-slate-500"><tr>
            <th className="px-3 py-2">Name typed by partner</th><th className="px-3 py-2">Partner codes</th><th className="px-3 py-2 text-right">Rows</th><th className="px-3 py-2">Last order</th><th className="px-3 py-2">Map to product</th></tr></thead>
            <tbody>{d.items.map((x) => (
              <tr key={x.key} className="border-t align-top" data-testid={`um-row-${x.key}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">{x.names.join(" / ")}</td>
                <td className="px-3 py-2 text-slate-600">{x.codes.join(", ")}</td>
                <td className="px-3 py-2 text-right font-semibold">{x.rows}</td>
                <td className="px-3 py-2 text-slate-500">{x.last ? x.last.slice(0, 10).split("-").reverse().join("/") : "—"}</td>
                <td className="px-3 py-2"><MapCell x={x} onDone={load} /></td>
              </tr>))}</tbody></table>
        </div>)}
    </div>
  );
};
